// @ts-check

// Vendor imports
const PropTypes = require('prop-types')
const React = require('react')
// Client V2 common imports
const { wrapDisplayName } = require('client-v2/common/components/utils')
const useSymbol = require('client-v2/common/hooks/symbol')
const tag = require('client-v2/common/utils/hyperscript')

const context = React.createContext({
  /** @type {(playerId: Symbol | null) => void} */
  setDisclosedPlayerId: () => {},
  /** @type {Symbol | null} */
  disclosedPlayerId: null
})

/**
 * React Component that provides the embedded player context.
 * @param {Object} props
 * @param {React.ElementType} props.children
 */
function EmbeddedPlayerManager(props) {
  const [ disclosedPlayerId, setDisclosedPlayerId ] = React.useState(null)
  return (
    tag(context.Provider, {
      value: {
        disclosedPlayerId,
        setDisclosedPlayerId
      }
    },
      props.children
    )
  )
}

EmbeddedPlayerManager.propTypes = {
  children: PropTypes.node
}

/**
 * React HOC to inject the `undiscloseEmbeddedPlayer` callback as a prop.
 * @param {React.Component} wrappedComponent
 * @returns {React.FunctionComponent}
 */
function withUndiscloseEmbeddedPlayer(wrappedComponent) {
  /**
   * @param {Object} props
   */
  function WithUndiscloseEmbeddedPlayer(props) {
    const undiscloseEmbeddedPlayer =
      useEmbeddedPlayerUndiscloseCallback()
    return tag(wrappedComponent, { ...props, undiscloseEmbeddedPlayer })
  }
  WithUndiscloseEmbeddedPlayer.displayName = wrapDisplayName(
    WithUndiscloseEmbeddedPlayer,
    wrappedComponent
  )
  return React.memo(WithUndiscloseEmbeddedPlayer)
}

/**
 * React Hook to memoize the `undisclose` action,
 * which undiscloses the current EmbeddedPlayer instance (if disclosed).
 * @returns {() => void}
 */
function useEmbeddedPlayerUndiscloseCallback() {
  const { setDisclosedPlayerId } = React.useContext(context)
  return React.useCallback(
    () => {
      setDisclosedPlayerId(null)
    },
    [ setDisclosedPlayerId ]
  )
}

/**
 * React Hook that managed embedded player disclosure state/callbacks.
 *
 * **IMPORTANT** YouTube Compliance:
 * Only one embed should be shown at at time!
 * @param {Object} options
 * @param {() => void} [options.onDisclose]
 * @param {() => void} [options.onUndisclose]
 * @returns {[ boolean, (event: Event) => void ]}
 */
function useEmbeddedPlayerDisclosure({
  onDisclose,
  onUndisclose
}) {
  const playerId = useSymbol()
  const { disclosedPlayerId, setDisclosedPlayerId } = React.useContext(context)
  const isDisclosed = disclosedPlayerId === playerId
  const onClick = useEmbeddedPlayerOnClickCallback({
    playerId,
    setDisclosedPlayerId,
    onDisclose
  })
  useEmbeddedPlayerOnUndiscloseEffect({
    isDisclosed,
    onUndisclose
  })
  return [ isDisclosed, onClick ]
}

/**
 * React Hook to memoize the EmbeddedPlayer `onClick` Event callback.
 * @param {Object} options
 * @param {Symbol} options.playerId
 * @param {(playerId: Symbol) => void} options.setDisclosedPlayerId
 * @param {() => void} [options.onDisclose]
 */
function useEmbeddedPlayerOnClickCallback({
  playerId,
  setDisclosedPlayerId,
  onDisclose
}) {
  return React.useCallback(
    event => {
      event.stopPropagation()
      setDisclosedPlayerId(playerId)
      if (onDisclose) {
        onDisclose()
      }
    },
    [ playerId, setDisclosedPlayerId, onDisclose ]
  )
}

/**
 * React Hook to perform the `onUndisclose` effect when
 * the `isDisclosed` state changes to `false`.
 * @param {Object} options
 * @param {boolean} options.isDisclosed
 * @param {() => void} [options.onUndisclose]
 */
function useEmbeddedPlayerOnUndiscloseEffect({
  isDisclosed,
  onUndisclose
}) {
  const prevDisclosedRef = React.useRef(false)
  React.useEffect(
    () => {
      if (
        onUndisclose &&
        !isDisclosed &&
        prevDisclosedRef.current
      ) {
        onUndisclose()
      }
      prevDisclosedRef.current = isDisclosed
    },
    [ isDisclosed, onUndisclose ]
  )
}

module.exports = {
  EmbeddedPlayerManager,
  useEmbeddedPlayerDisclosure,
  useEmbeddedPlayerUndiscloseCallback,
  withUndiscloseEmbeddedPlayer
}
